import { FC, Fragment, useMemo } from 'react';
import { BKJAvatar, BKJButton } from '@bookjane2/bookjane-design-library';
import { Flex } from 'components/Flex';
import { BKJSideModalFooter } from 'components/BKJSideModalFooter';
import { useTranslation } from 'hooks/useTranslation';
import { useAwardTeamMemberContext } from 'modals/ShiftModal/src/views/AwardTeamMemberView/AwardTeamMemberView.data.context';
import { useAwardTeamMemberViewFormContext } from 'modals/ShiftModal/src/views/AwardTeamMemberView/AwardTeamMemberView.form.context';
import {
  AwardShiftBlockDataProvider,
  useAwardShiftBlockDataContext,
} from 'modals/ShiftModal/src/views/AwardShiftBlockView/AwardShiftBlockView.data.context';
import {
  AwardShiftBlockFormProvider,
  useAwardShiftBlockFormContext,
} from 'modals/ShiftModal/src/views/AwardShiftBlockView/AwardShiftBlockView.form.context';
import { ShiftBlockOrdersList } from 'modals/ShiftModal/components/ShiftBlockOrdersList';
import {
  Heading,
  Description,
  SectionLabel,
  JaneCard,
  JaneName,
  JaneCommunity,
} from './AwardShiftBlockView.styled';

interface SelectedJane {
  id: string;
  firstName: string;
  lastName: string;
  avatarUrls?: { origin?: string } | null;
  primaryCommunity?: { name: string } | null;
}

const AwardShiftBlockViewInner: FC = () => {
  const { t } = useTranslation();
  const { data } = useAwardTeamMemberContext();
  const order = data?.order;
  const { selectedJanes } = useAwardTeamMemberViewFormContext();
  const { orders, isLoading, hasNextPage, loadMore } = useAwardShiftBlockDataContext();
  const { onSubmit } = useAwardShiftBlockFormContext();

  const selectedJane = useMemo((): SelectedJane | null => {
    if (!order?.bids || !selectedJanes?.length) return null;
    const janeId = selectedJanes[0];
    const bid = order.bids.find((b) => b?.jane?.id === janeId);
    return (bid?.jane as SelectedJane) ?? null;
  }, [order?.bids, selectedJanes]);

  return (
    <Fragment>
      <Flex flexDirection="column" gap="32px" padding="40px 32px 0">
        <Heading>{t('shift_modal:AWARD_SHIFT_BLOCK')}</Heading>
        <Description>{t('shift_modal:AWARD_SHIFT_BLOCK_DESCRIPTION')}</Description>
        <Flex flexDirection="column" gap="8px">
          <SectionLabel>{t('shift_modal:AWARD_SHIFT_BLOCK_TO')}</SectionLabel>
          <JaneCard>
            <BKJAvatar
              src={selectedJane?.avatarUrls?.origin || ''}
              fallbackAvatarColor="TextDarkGrey"
              width="48px"
              height="48px"
            />
            <Flex flexDirection="column">
              <JaneName>
                {selectedJane?.firstName} {selectedJane?.lastName}
              </JaneName>
              <JaneCommunity>{selectedJane?.primaryCommunity?.name || ''}</JaneCommunity>
            </Flex>
          </JaneCard>
        </Flex>
      </Flex>
      <Flex flexDirection="column" gap="16px" padding="24px 32px" flex="1" minHeight="0">
        <ShiftBlockOrdersList
          orders={orders}
          isLoading={isLoading}
          hasNextPage={hasNextPage}
          loadMore={loadMore}
        />
      </Flex>
      <BKJSideModalFooter justifyContent="flex-end">
        <BKJButton variant="PurpleSolid" width="180px" onClick={onSubmit}>
          {t('shift_modal:AWARD')}
        </BKJButton>
      </BKJSideModalFooter>
    </Fragment>
  );
};

export const AwardShiftBlockView: FC = () => {
  return (
    <AwardShiftBlockDataProvider>
      <AwardShiftBlockFormProvider>
        <AwardShiftBlockViewInner />
      </AwardShiftBlockFormProvider>
    </AwardShiftBlockDataProvider>
  );
};
